import fetch from 'isomorphic-fetch';
import _cloneDeep from 'lodash/cloneDeep';
import _find from 'lodash/find';
import _isEqual from 'lodash/isEqual';
import _pick from 'lodash/pick';
import _sortBy from 'lodash/sortBy';
import _uniqBy from 'lodash/uniqBy';
import _without from 'lodash/without';

import { showErrorBar } from 'actions/errorBar';
import { updateShareVideo } from 'actions/shareVideo';
import {
  enterTrimMode,
  leaveTrimMode,
  updateCurrentTrimRange,
  updateSelectedTrimBound,
  updateSelectedTrimRange,
  updateTrimNewChanges,
  updateTrimOriginalRanges,
  updateTrimRanges,
  updateTrimRedoStack,
  updateTrimUndoStack,
} from 'actions/trimming';
import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerPause,
  requestVideoPlayerPlay,
  requestVideoPlayerVolume,
  updateTheaterMode,
  updateVideoPlayerCurrentTime,
} from 'actions/videoPlayer';
import { ERROR } from 'constants/error-severities';
import { TRIM_PUBLISHED } from 'constants/events';
import { HEADER_CONTENT_TYPE } from 'constants/http';
import { TRIM_ACTION_DISPATCHED } from 'constants/messages';
import { JSON as JSON_MIME } from 'constants/mimes';
import {
  ADD_TRIM_OPERATION,
  ENTER_TRIM_MODE_OPERATION,
  LEAVE_TRIM_MODE_OPERATION,
  REDO_TRIM_OPERATION,
  REMOVE_TRIM_OPERATION,
  UNDO_TRIM_OPERATION,
  UPDATE_CURRENT_TRIM_RANGE_OPERATION,
  UPDATE_SELECTED_TRIM_BOUND_OPERATION,
  UPDATE_SELECTED_TRIM_RANGE_OPERATION,
  UPDATE_TRIM_OPERATION,
} from 'constants/trimming';
import {
  updateVideoCreator,
  updateVideoPlayerSourceCreator,
} from 'creators/videoPlayer';
import * as analytics from 'utilities/analytics';
import * as sentry from 'utilities/sentry';
import {
  convertFullTimeToTrimTime,
  convertTrimTimeToFullTime,
  getTrimEventProperties,
  getTrimVideoDuration,
} from 'utilities/trimming';

const UNKNOWN_TRIM_OPERATION = new Error('unknown trim operation');

const MINIMUM_TOTAL_VIDEO_TIME = 1; // 1 second

/**
  For the undo/redo actions, we have a `redo_stack` and `undo_stack`. The basic
  idea is:

  1. Each time we perform a *new* action, we should add the compliment of that
     action to the `undo_stack` and clear everything in the `redo_stack`.

     * Add a trim range with Id 4444
     * Add an operation to remove the trim with Id of 4444

  2. Each time we perform a redo/undo, we should pop off the respective stack,
     apply the operation, find the compliment of that operation, and then add to
     the other stack. An example that builds off the previous example.

     * We call undo
     * Pop off the `undo_stack` and then run the operation which removes the trim
       with Id 4444
     * Find the compliment, which is adding a Trim of Id 4444
     * Add "add trim Id 4444" to the redo stack
**/

function callCreatorForTrimOperation(trimOperation) {
  const { trimRange, type } = trimOperation;

  switch (type) {
    case ADD_TRIM_OPERATION:
      return addTrimRangeCreator(trimRange, true);
    case REMOVE_TRIM_OPERATION:
      return removeTrimRangeCreator(trimRange.id, true);
    case UPDATE_TRIM_OPERATION:
      return updateTrimRangeCreator(trimRange, true);
    default:
      sentry.logException(
        UNKNOWN_TRIM_OPERATION,
        `unkown trim operation ${type}`
      );

      return {};
  }
}

function getComplementTrimOperation(trimOperation, currentTrimRange) {
  const { trimRange, type } = trimOperation;

  switch (type) {
    case ADD_TRIM_OPERATION:
      return { type: REMOVE_TRIM_OPERATION, trimRange };
    case REMOVE_TRIM_OPERATION:
      return { type: ADD_TRIM_OPERATION, trimRange };
    case UPDATE_TRIM_OPERATION:
      return { type: UPDATE_TRIM_OPERATION, trimRange: currentTrimRange };
    default:
      sentry.logException(
        UNKNOWN_TRIM_OPERATION,
        `unkown trim operation ${type}`
      );

      return {};
  }
}

function sortRanges(ranges) {
  return _sortBy(ranges, range => range.from);
}

export const enterTrimModeCreator = (trimRanges, postMessage = true) => {
  return (dispatch, getState) => {
    dispatch(updateTheaterMode(false));
    const trimRangesWithoutIds = trimRanges.map(trimRange => {
      return _pick(trimRange, 'from', 'to');
    });

    const trimRangesWithIds = trimRanges.map((trimRange, trimRangeIndex) => {
      const newTrimRange = { ...trimRange };

      if (newTrimRange.id == null) {
        // ensure each ID is unique
        newTrimRange.id = Number(new Date()) + trimRangeIndex;
      }

      return newTrimRange;
    });

    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: ENTER_TRIM_MODE_OPERATION,
          trimRanges: trimRangesWithIds,
        },
        '*'
      );
    }

    dispatch(updateTrimOriginalRanges(trimRangesWithoutIds));
    dispatch(updateTrimRangesCreator(trimRangesWithIds));
    dispatch(enterTrimMode());

    const {
      current_time: trimVideoTime,
      duration: currentDuration,
      video,
      volume,
    } = getState().video_player;

    let duration = currentDuration;

    // try to take the full duration if possible - there's a chance the video
    // player duration is the trimmed duration
    if (video.video_properties.duration != null) {
      duration = video.video_properties.duration;
    }

    // the video may be trimmed, so we have to convert if it is
    const fullVideoTime = convertTrimTimeToFullTime(
      trimVideoTime * 1000,
      trimRanges
    );

    // we need to force the original video source (not the trimmed source)
    return dispatch(updateVideoPlayerSourceCreator(true))
      .then(() => {
        const fullVideoTimeInSeconds = fullVideoTime / 1000;

        // load from the time we were at before in the video, play the video, and
        // wait for the video to start playing so the control bar gets rendered
        dispatch(requestVideoPlayerCurrentTime(fullVideoTimeInSeconds));

        // check that the video will not be immediately paused if we're at the end,
        // causing an error to be thrown and the rest of the promise chain to not
        // be executed
        if (fullVideoTimeInSeconds < duration) {
          dispatch(requestVideoPlayerPlay());
        }

        return new Promise(resolve => {
          const checkHasStartedInterval = window.setInterval(() => {
            const { has_started: hasStarted } = getState().video_player;

            if (!hasStarted) {
              return;
            }

            window.clearInterval(checkHasStartedInterval);
            resolve();
          }, 50);
        });
      })
      .then(() => {
        // pause the video and enter trim mode
        dispatch(requestVideoPlayerPause());
      })
      .then(() => {
        // restore the previous volume before entering trim mode
        return dispatch(requestVideoPlayerVolume(volume));
      });
  };
};

export const leaveTrimModeCreator = (
  updatedVideo = null,
  postMessage = true
) => {
  return (dispatch, getState) => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: LEAVE_TRIM_MODE_OPERATION,
          updatedVideo,
        },
        '*'
      );
    }

    // first leave trim mode so the creators below can use the `in_trim_mode`
    // trimming state if need be (e.g. updateVideoCreator checking if it should
    // begin polling for the video source or forego it if a user is waiting on a
    // trim to complete)
    dispatch(leaveTrimMode());

    const { current_time: currentTime } = getState().video_player;
    const { trim_ranges: trimRanges } = getState().trimming;

    // if we're coming from the patchTrimVideoCreator
    if (updatedVideo != null) {
      // if we patched the video, start from the beginning
      dispatch(updateVideoPlayerCurrentTime(0));

      dispatch(updateVideoCreator(updatedVideo));
    } else {
      // if we're just leaving trim mode, then continue where we left off
      dispatch(
        updateVideoPlayerCurrentTime(
          convertFullTimeToTrimTime(currentTime * 1000, trimRanges) / 1000
        )
      );

      dispatch(updateVideoPlayerSourceCreator());
    }
  };
};

export const addTrimRangeCreator = (
  trimRange,
  fromUndoOrRedo = false,
  postMessage = true
) => {
  return (dispatch, getState) => {
    const {
      trim_ranges: trimRanges,
      undo_stack: undoStack,
    } = getState().trimming;

    const newTrimRange = { ...trimRange };

    // undo/redo operations may reuse old trim Ids, we should keep those
    if (!fromUndoOrRedo && trimRange.id == null) {
      newTrimRange.id = Number(new Date());
    }

    const newRanges = sortRanges([..._cloneDeep(trimRanges), newTrimRange]);

    if (!fromUndoOrRedo) {
      const newUndoStack = [
        ..._cloneDeep(undoStack),
        getComplementTrimOperation({
          type: ADD_TRIM_OPERATION,
          trimRange: newTrimRange,
        }),
      ];

      dispatch(updateTrimUndoStack(newUndoStack));
      dispatch(updateTrimRedoStack([]));

      // keep the share video control bar and embed player in sync
      if (postMessage) {
        window.top.postMessage(
          {
            type: TRIM_ACTION_DISPATCHED,
            operation: ADD_TRIM_OPERATION,
            trimRange: newTrimRange,
          },
          '*'
        );
      }
    }

    dispatch(updateTrimRangesCreator(newRanges));
  };
};

export const removeTrimRangeCreator = (
  trimRangeId,
  fromUndoOrRedo = false,
  postMessage = true
) => {
  return (dispatch, getState) => {
    const {
      current_trim_range: currentTrimRange,
      trim_ranges: trimRanges,
      undo_stack: undoStack,
    } = getState().trimming;

    const trimRange = _find(trimRanges, ({ id }) => id === trimRangeId);

    if (trimRange == null) {
      return;
    }

    const clonedRanges = _cloneDeep(_without(trimRanges, trimRange));
    const newRanges = sortRanges(clonedRanges);

    if (!fromUndoOrRedo) {
      const newUndoStack = [
        ..._cloneDeep(undoStack),
        getComplementTrimOperation({
          type: REMOVE_TRIM_OPERATION,
          trimRange,
        }),
      ];

      dispatch(updateTrimUndoStack(newUndoStack));
      dispatch(updateTrimRedoStack([]));

      // keep the share video control bar and embed player in sync
      if (postMessage) {
        window.top.postMessage(
          {
            type: TRIM_ACTION_DISPATCHED,
            operation: REMOVE_TRIM_OPERATION,
            id: trimRangeId,
          },
          '*'
        );
      }
    }

    dispatch(updateTrimRangesCreator(newRanges));

    if (currentTrimRange != null && currentTrimRange.id !== trimRangeId) {
      dispatch(updateCurrentTrimRangeCreator(currentTrimRange));
    }
  };
};

export const updateTrimRangeCreator = (
  newTrimRange,
  fromUndoOrRedo = false,
  postMessage = true
) => {
  return (dispatch, getState) => {
    const {
      trim_ranges: trimRanges,
      undo_stack: undoStack,
    } = getState().trimming;

    const currentTrimRange = _find(
      trimRanges,
      ({ id }) => id === newTrimRange.id
    );

    if (currentTrimRange == null) {
      return;
    }

    const newRanges = sortRanges([
      ..._cloneDeep(_without(trimRanges, currentTrimRange)),
      newTrimRange,
    ]);

    if (!fromUndoOrRedo) {
      const newUndoStack = [
        ..._cloneDeep(undoStack),
        getComplementTrimOperation(
          {
            type: UPDATE_TRIM_OPERATION,
          },
          currentTrimRange
        ),
      ];

      dispatch(updateTrimUndoStack(newUndoStack));
      dispatch(updateTrimRedoStack([]));

      // keep the share video control bar and embed player in sync
      if (postMessage) {
        window.top.postMessage(
          {
            type: TRIM_ACTION_DISPATCHED,
            operation: UPDATE_TRIM_OPERATION,
            trimRange: newTrimRange,
          },
          '*'
        );
      }
    }

    dispatch(updateTrimRangesCreator(newRanges));
  };
};

export const undoTrimOperationCreator = (postMessage = true) => {
  return (dispatch, getState) => {
    const {
      redo_stack: redoStack,
      trim_ranges: trimRanges,
      undo_stack: undoStack,
    } = getState().trimming;

    const newUndoStack = _cloneDeep(undoStack);
    const undoOperation = newUndoStack.pop();

    dispatch(callCreatorForTrimOperation(undoOperation));
    dispatch(updateTrimUndoStack(newUndoStack));

    const currentTrimRange = _find(trimRanges, ({ id }) => {
      return id === undoOperation.trimRange.id;
    });

    const redoOperation = getComplementTrimOperation(
      undoOperation,
      currentTrimRange
    );
    const newRedoStack = [..._cloneDeep(redoStack), redoOperation];

    dispatch(updateTrimRedoStack(newRedoStack));

    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: UNDO_TRIM_OPERATION,
        },
        '*'
      );
    }
  };
};

export const redoTrimOperationCreator = (postMessage = true) => {
  return (dispatch, getState) => {
    const {
      redo_stack: redoStack,
      trim_ranges: trimRanges,
      undo_stack: undoStack,
    } = getState().trimming;

    const newRedoStack = _cloneDeep(redoStack);
    const redoOperation = newRedoStack.pop();

    dispatch(callCreatorForTrimOperation(redoOperation));
    dispatch(updateTrimRedoStack(newRedoStack));

    const currentTrimRange = _find(trimRanges, ({ id }) => {
      return id === redoOperation.trimRange.id;
    });

    const undoOperation = getComplementTrimOperation(
      redoOperation,
      currentTrimRange
    );
    const newUndoStack = [..._cloneDeep(undoStack), undoOperation];

    dispatch(updateTrimUndoStack(newUndoStack));

    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: REDO_TRIM_OPERATION,
        },
        '*'
      );
    }
  };
};

export const patchTrimVideoCreator = (video, onFinish = () => {}) => {
  return (dispatch, getState) => {
    const { trim_ranges: trimRanges } = getState().trimming;
    const { id: videoId } = video;

    const { video_properties: videoProperties = {} } = video;
    const { duration } = videoProperties;

    const totalTrimTime = getTrimVideoDuration(duration, trimRanges);

    let minimumTotalTimeString = `${MINIMUM_TOTAL_VIDEO_TIME} second`;

    if (MINIMUM_TOTAL_VIDEO_TIME > 1) {
      minimumTotalTimeString += 's';
    }

    if (totalTrimTime < MINIMUM_TOTAL_VIDEO_TIME) {
      return window.alert(
        "Woops! You can't publish a video that's less than " +
          `${minimumTotalTimeString} long.`
      );
    }

    const body = JSON.stringify({
      processing_information: {
        trim_ranges: trimRanges.map(trimRange =>
          _pick(trimRange, 'from', 'to')
        ),
      },
    });

    return fetch(`/v1/videos/${videoId}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PATCH',
      body,
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw new Error(
            `Oops! Error trimming video - HTTP response: ${resp.status}`
          );
        }

        // ensure we got back a serializable return type
        return resp.json();
      })
      .then(patchedVideo => {
        // update the processing information for the share video view
        dispatch(updateShareVideo(patchedVideo));

        return dispatch(leaveTrimModeCreator(patchedVideo));
      })
      .then(() => {
        if (trimRanges.length > 0) {
          analytics.track(
            TRIM_PUBLISHED,
            getTrimEventProperties(video, trimRanges, duration)
          );
        }

        onFinish();
      })
      .catch(err => {
        sentry.logException(err, 'error in trim video creator');
        dispatch(showErrorBar(ERROR, err.toString()));
      });
  };
};

export const updateCurrentTrimRangeCreator = (
  trimRange,
  postMessage = true
) => {
  return dispatch => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: UPDATE_CURRENT_TRIM_RANGE_OPERATION,
          trimRange,
        },
        '*'
      );
    }

    return dispatch(updateCurrentTrimRange(trimRange));
  };
};

export const updateSelectedTrimBoundCreator = (
  trimBound,
  postMessage = true
) => {
  return dispatch => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: UPDATE_SELECTED_TRIM_BOUND_OPERATION,
          trimBound,
        },
        '*'
      );
    }

    return dispatch(updateSelectedTrimBound(trimBound));
  };
};

export const updateSelectedTrimRangeCreator = (
  trimRange,
  postMessage = true
) => {
  return dispatch => {
    // keep the share video control bar and embed player in sync
    if (postMessage) {
      window.top.postMessage(
        {
          type: TRIM_ACTION_DISPATCHED,
          operation: UPDATE_SELECTED_TRIM_RANGE_OPERATION,
          trimRange,
        },
        '*'
      );
    }

    return dispatch(updateSelectedTrimRange(trimRange));
  };
};

export const updateTrimRangesCreator = newTrimRanges => {
  return (dispatch, getState) => {
    const { original_ranges: originalRanges } = getState().trimming;

    // due to combining redux stores for iframe and window we get duplicate
    // actions and thus multiple trim ranges with the same id, we put in this
    // stopgap till we remove the extraneous message passing and dupe actions
    const actualNewTrimRanges = _uniqBy(newTrimRanges, 'id');

    const newTrimRangesNoIds = actualNewTrimRanges.map(trimRange => {
      return _pick(trimRange, 'from', 'to');
    });

    const newChanges = !_isEqual(newTrimRangesNoIds, originalRanges);

    dispatch(updateTrimNewChanges(newChanges));
    dispatch(updateTrimRanges(actualNewTrimRanges));
  };
};
